import PageLayout from "@/components/feature/PageLayout";
import CTAButton from "@/components/feature/CTAButton";
import {
  registerProducts,
  type RegisterProduct,
} from "@/mocks/registerProducts";
import { activeSeason } from "@/mocks/activeSeason";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { getActiveSeason, getRegisterProducts } from "@/services/content";

// Temporarily hidden programs. Remove an id from this list to restore the card.
// "reg-akm-training" = AKM Training (Tues–Thurs) — hidden by request, not deleted.
const HIDDEN_PRODUCT_IDS: string[] = ["reg-akm-training"];

function ProductCard({ product }: { product: RegisterProduct }) {
  // Athletes First usa su propio rojo para que se distinga de los programas de Triumph.
  const isA1 = product.accent === "a1";
  const ctaVariant =
    product.ctaStyle === "secondary" ? "secondary" : "primary";

  const ctaLabel =
    product.ctaLabel ??
    (product.ctaStyle === "secondary" ? "Join Waitlist" : "Enroll Now");

  const isMailto = product.ctaLink?.startsWith("mailto:");

  return (
    <div
      className={`bg-gunmetal-50 border flex flex-col md:h-full transition-colors ${
        isA1
          ? "border-onyx-400 border-t-[3px] border-t-[#F20D2E] hover:border-[#F20D2E]/50"
          : "border-onyx-400 hover:border-strike-500/40"
      }`}
    >
      {/* Image banner */}
      {product.imageUrl && (
        <div className="relative overflow-hidden aspect-[2/1] max-h-48 md:max-h-none">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-50/80 via-gunmetal-50/20 to-transparent"></div>
        </div>
      )}

      {/* Badge */}
      {product.badge && (
        <div className={`px-5 py-2 border-b border-onyx-400 ${isA1 ? "bg-[#F20D2E]/12" : "bg-strike-500/10"}`}>
          <span className={`font-body text-[10px] font-bold tracking-[0.2em] uppercase ${isA1 ? "text-[#FF4A63]" : "text-strike-500"}`}>
            {product.badge}
          </span>
        </div>
      )}

      <div className="p-5 md:p-6 flex flex-col md:flex-1">
        <h3 className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-wide leading-tight">
          {product.name}
        </h3>
        <p className="font-body text-white/40 text-xs mt-1 mb-4 uppercase tracking-wider">
          {product.dateRange}
        </p>
        <p className="font-body text-white/60 text-sm leading-relaxed md:flex-1">
          {product.description}
        </p>

        <div className="mt-4 md:mt-6 pt-5 border-t border-onyx-400 flex flex-col gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-strike-500 text-3xl font-bold">
              {typeof product.price === "number"
                ? `$${product.price}`
                : product.price}
            </span>
            {product.priceNote && (
              <span className="font-body text-white/30 text-xs">
                {product.priceNote}
              </span>
            )}
            {!product.priceNote && product.season !== "evergreen" && typeof product.price === "number" && (
              <span className="font-body text-white/30 text-xs uppercase">
                / season
              </span>
            )}
          </div>
          {isMailto ? (
            <CTAButton
              variant={ctaVariant}
              size="md"
              className={isA1 ? "!bg-[#F20D2E] !text-white hover:!bg-[#C70A24]" : ""}
              href={product.ctaLink}
            >
              {ctaLabel}
            </CTAButton>
          ) : (
            <CTAButton
              variant={ctaVariant}
              size="md"
              className={isA1 ? "!bg-[#F20D2E] !text-white hover:!bg-[#C70A24]" : ""}
              to={product.ctaLink}
            >
              {ctaLabel}
            </CTAButton>
          )}
          {product.footnote && (
            <p className="font-body text-white/25 text-[11px] leading-relaxed -mt-2">
              {product.footnote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  // Both fall back to src/mocks/ until Supabase is wired up — see README-ADRIAN.md.
  const { data: season } = useSupabaseData(getActiveSeason, activeSeason);
  const { data: products } = useSupabaseData(getRegisterProducts, registerProducts);

  // Season engine: only show programs tagged for the active season, plus
  // "evergreen" programs (like Private Training) that run year-round. This
  // is the filter the admin dashboard is meant to drive — flip a program's
  // `season` in Supabase and it appears/disappears here automatically.
  const visibleProducts = products
    .filter((product) => !HIDDEN_PRODUCT_IDS.includes(product.id))
    .filter(
      (product) => product.season === season.name || product.season === "evergreen"
    );

  return (
    <PageLayout>
      {/* Header */}
      <section className="w-full px-4 md:px-8 lg:px-12 pt-28 md:pt-32 pb-8 bg-onyx-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-strike-500"></div>
            <span className="font-body text-strike-500 text-xs font-semibold tracking-[0.2em] uppercase">
              Our Programs
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase tracking-tight">
            Our Programs
          </h1>
          <p className="font-body text-white/50 text-sm md:text-base mt-3 max-w-xl">
            Select a program below to secure your spot.
          </p>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-onyx-50">
        <div className="max-w-6xl mx-auto">
          {/* Programs label */}
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-strike-500 inline-block"></span>
            <span className="font-body text-white/50 text-xs font-semibold uppercase tracking-wider">
              {season.name} Season — Active Programs
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
            {visibleProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {visibleProducts.length === 0 && (
            <div className="text-center py-16 border border-onyx-400">
              <p className="font-body text-white/30 text-sm">
                No programs available at this time. Check back soon.
              </p>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}